import { faCheck, faMinus, faPlus } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { StringID } from "userful-chronos-app-common-js/dist/models/common";
import Button from "../button/Button";
import { ISimpleTableItem } from "./simpleTable";
import "./SimpleTable.scss";

interface IProps {
    header: string;
    items: ISimpleTableItem[];
    selectedItems: StringID[]; // expects { value: string }
    onSelectionChange: (selectedItems: StringID[]) => void;
}

export default function SimpleTable(props: IProps) {
    const { items, selectedItems, header, onSelectionChange } = props;

    const allSelected = items.length > 0 && selectedItems.length === items.length;
    const noneSelected = selectedItems.length === 0;
    const someSelected = !noneSelected && !allSelected;

    const selectAll = () => onSelectionChange(items.map((i) => i.id));
    const clearAll = () => onSelectionChange([]);

    const toggleAll = () => {
        // If none or some are selected -> select all. If all are selected -> clear.
        if (allSelected) clearAll();
        else selectAll();
    };

    const isSelected = (id: StringID) => selectedItems.some((s) => s.value === id.value);

    const toggleOne = (id: StringID) => {
        const currentlySelected = isSelected(id);
        let next: StringID[];
        if (currentlySelected) {
            // Remove by value (not by object reference)
            next = selectedItems.filter((s) => s.value !== id.value);
        } else {
            // Add uniquely by value
            if (selectedItems.some((s) => s.value === id.value)) next = selectedItems;
            else next = [...selectedItems, id];
        }
        onSelectionChange(next);
    };

    return (
        <>
            <div className="userful-simple-table noselect">
                <div className="userful-simple-table-header">
                    <Button
                        iconOnly
                        variant="ghost"
                        onClick={toggleAll}
                        aria-label={allSelected ? "Clear selection" : "Select all"}
                    >
                        <FontAwesomeIcon icon={allSelected ? faMinus : faPlus} />
                    </Button>
                    <h1>{header}</h1>
                </div>

                <div className="userful-simple-table-body">
                    {items.map((item) => {
                        const selected = isSelected(item.id);
                        return (
                            <div
                                key={item.id.value}
                                className={`userful-simple-table-row ${
                                    selected ? "userful-simple-table-selected-row" : ""
                                }`}
                                onClick={() => toggleOne(item.id)}
                                role="button"
                                tabIndex={0}
                                aria-pressed={selected}
                                aria-selected={selected}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        toggleOne(item.id);
                                    }
                                }}
                            >
                                <div className="userful-simple-table-icon">
                                    <FontAwesomeIcon icon={selected ? faCheck : faPlus} />
                                </div>
                                <p>{item.name}</p>
                            </div>
                        );
                    })}
                </div>
            </div>

            {selectedItems.length > 0 && (
                <div className="userful-simple-table-footer noselect">
                    <p>
                        Selected {allSelected ? "all " : ""}
                        {selectedItems.length}
                        {!allSelected ? ` out of ${items.length} ` : " "}
                        item(s).
                    </p>
                </div>
            )}
        </>
    );
}
